import { fetchWithErrorHandling, buildPostOptions, buildGetOptions } from "../FetchUtils";
import { useAuthUser } from "react-auth-kit";
import { useContext } from "react";
import { AppContext } from "utils/AppContext";
import { getBaseURL } from "utils/Environment";

export enum GameConfigState {
  DRAFT = "DRAFT",
  IN_REVIEW = "IN_REVIEW",
  APPROVED = "APPROVED",
  MERGED = "MERGED",
  DISCARDED = "DISCARDED",
}

export interface PullRequestData {
  id: string;
  state: string;
  merged_at?: string;
  number: number;
  title: string;
}

export class IGameConfigBranchInfo {
  constructor(
    public branch: string,
    public state: GameConfigState,
    public pullRequest: PullRequestData | undefined,
    public lastCommitHash: string | undefined,
  ) {}
}

// Define interfaces for wad templates
export interface IWadTemplate {
  labels: string[];
  filePatterns: string[];
  relatedAddressableGroup?: string;
  description?: string;
}

export type WadTemplateList = IWadTemplate[];

export const listBranches = async (accessToken: string, baseURL: string) => {
  return fetchWithErrorHandling(baseURL + "v1/game-config/branch/all", buildGetOptions(accessToken));
};

export const createBranch = async (accessToken: string, baseURL: string, branch: string) => {
  return fetchWithErrorHandling(baseURL + "v1/game-config/branch/create", buildPostOptions(accessToken, { branch }));
};

export const getBranchInfo = async (
  accessToken: string,
  baseURL: string,
  branch: string,
): Promise<IGameConfigBranchInfo> => {
  return fetchWithErrorHandling(
    baseURL + `v1/game-config/branch/${encodeURIComponent(branch)}`,
    buildGetOptions(accessToken),
  );
};

export const selectBranch = async (accessToken: string, baseURL: string, branch: string) => {
  return fetchWithErrorHandling(baseURL + "v1/game-config/branch/select", buildPostOptions(accessToken, { branch }));
};

export const revertBranch = async (accessToken: string, baseURL: string, branch: string) => {
  return fetchWithErrorHandling(baseURL + "v1/game-config/branch/revert", buildPostOptions(accessToken, { branch }));
};

export const commitBranch = async (accessToken: string, baseURL: string, branch: string, message: string) => {
  return fetchWithErrorHandling(
    baseURL + "v1/game-config/branch/commit",
    buildPostOptions(accessToken, { branch, message }),
  );
};

export const submitBranch = async (
  accessToken: string,
  baseURL: string,
  branch: string,
  message?: string,
  ticketId?: string,
) => {
  return fetchWithErrorHandling(
    baseURL + "v1/game-config/branch/submit",
    buildPostOptions(accessToken, { branch, message, ticketId }),
  );
};

export const deleteBranch = async (accessToken: string, baseURL: string, branch: string) => {
  return fetchWithErrorHandling(baseURL + "v1/game-config/branch/delete", buildPostOptions(accessToken, { branch }));
};

export const getWadTemplates = async (
  accessToken: string,
  baseURL: string,
  branch: string,
  userEmail?: string,
  userName?: string,
): Promise<WadTemplateList> => {
  const response = await fetchWithErrorHandling(
    baseURL + "v1/game-config/get-wad-templates",
    buildPostOptions(accessToken, { branch, userEmail, userName }),
  );
  return response.templates;
};

export const setWadTemplates = async (
  accessToken: string,
  baseURL: string,
  branch: string,
  templates: WadTemplateList,
  userEmail?: string,
  userName?: string,
): Promise<void> => {
  return fetchWithErrorHandling(
    baseURL + "v1/game-config/set-wad-templates",
    buildPostOptions(accessToken, { branch, templates, userEmail, userName }),
  );
};

export interface IIdentifiableStaticData {
  id: string;
  sdId: string;
  data: unknown;
}

export interface IStaticDataFileMapping {
  id: string;
  database?: string;
  currentSDId?: string;
  currentId?: string;
}

export interface IDiffItem {
  path: string;
  id: string | null;
  sdId: string | null;
  diff: string;
  oldContent: string | null;
  newContent: string | null;
}

export interface IDiffResponse {
  added: IDiffItem[];
  removed: IDiffItem[];
  updated: IDiffItem[];
}

export interface IReportFile {
  exists: boolean;
  content: string | null;
  error?: string;
}

export interface ILatestReportsResponse {
  remainingIdsFile: IReportFile;
  importOutputLogFile: IReportFile;
  deployOutputLogFile: IReportFile;
  legacyImportFile: IReportFile;
  commitCount: number;
}

export const deleteItem = async (
  accessToken: string,
  baseURL: string,
  branch: string,
  sdId: string,
  userEmail?: string,
  userName?: string,
): Promise<boolean> => {
  const response = await fetchWithErrorHandling(
    baseURL + "v1/game-config/delete-item",
    buildPostOptions(accessToken, { branch, sdId, userEmail, userName }),
  );
  return response.success;
};

export const importSpreadsheets = async (
  accessToken: string,
  baseURL: string,
  branch: string,
  hubSpreadsheetUrl?: string,
  customSpreadsheetUrls?: string[],
  userEmail?: string,
  userName?: string,
  toolboxBuildId?: string,
  toolboxBuildBranch?: string,
  mappingData?: IStaticDataFileMapping[],
): Promise<{ remainingIds: IStaticDataFileMapping[]; output: string }> => {
  const response = await fetchWithErrorHandling(
    baseURL + "v1/game-config/import-spreadsheets",
    buildPostOptions(accessToken, {
      branch,
      hubSpreadsheetUrl,
      customSpreadsheetUrls,
      userEmail,
      userName,
      toolboxBuildId,
      toolboxBuildBranch,
      mappingData,
    }),
  );
  return {
    remainingIds: response.remainingIds,
    output: response.output,
  };
};

export const deployImportedData = async (
  accessToken: string,
  baseURL: string,
  branch: string,
  userEmail?: string,
  userName?: string,
  environmentName?: string,
): Promise<{ output: string }> => {
  const response = await fetchWithErrorHandling(
    baseURL + "v1/game-config/deploy-imported-data",
    buildPostOptions(accessToken, {
      branch,
      userEmail,
      userName,
      environmentName,
    }),
  );
  return {
    output: response.output,
  };
};

export const listItems = async (
  accessToken: string,
  baseURL: string,
  branch: string,
  userEmail?: string,
  userName?: string,
): Promise<IIdentifiableStaticData[]> => {
  const response = await fetchWithErrorHandling(
    baseURL + "v1/game-config/list-items",
    buildPostOptions(accessToken, { branch, userEmail, userName }),
  );
  return response.items;
};

export const getDiff = async (
  accessToken: string,
  baseURL: string,
  branch: string,
  userEmail?: string,
  userName?: string,
): Promise<IDiffResponse> => {
  return fetchWithErrorHandling(
    baseURL + "v1/game-config/get-diff",
    buildPostOptions(accessToken, { branch, userEmail, userName }),
  );
};

export const getLatestReports = async (
  accessToken: string,
  baseURL: string,
  branch: string,
  userEmail?: string,
  userName?: string,
): Promise<ILatestReportsResponse> => {
  return fetchWithErrorHandling(
    baseURL + "v1/game-config/get-latest-reports",
    buildPostOptions(accessToken, { branch, userEmail, userName }),
  );
};

export const useGameConfigService = () => {
  const authUser = useAuthUser();
  const accessToken = authUser()?.access_token;
  const appContext = useContext(AppContext);

  return {
    listBranches: () => listBranches(accessToken, getBaseURL(appContext)),
    createBranch: (branch: string) => createBranch(accessToken, getBaseURL(appContext), branch),
    getBranchInfo: (branch: string) => getBranchInfo(accessToken, getBaseURL(appContext), branch),
    selectBranch: (branch: string) => selectBranch(accessToken, getBaseURL(appContext), branch),
    revertBranch: (branch: string) => revertBranch(accessToken, getBaseURL(appContext), branch),
    commitBranch: (branch: string, message: string) => commitBranch(accessToken, getBaseURL(appContext), branch, message),
    submitBranch: (branch: string, message?: string, ticketId?: string) =>
      submitBranch(accessToken, getBaseURL(appContext), branch, message, ticketId),
    deleteBranch: (branch: string) => deleteBranch(accessToken, getBaseURL(appContext), branch),
    getWadTemplates: (branch: string, userEmail?: string, userName?: string) =>
      getWadTemplates(accessToken, getBaseURL(appContext), branch, userEmail, userName),
    setWadTemplates: (branch: string, templates: WadTemplateList, userEmail?: string, userName?: string) =>
      setWadTemplates(accessToken, getBaseURL(appContext), branch, templates, userEmail, userName),
    deleteItem: (branch: string, sdId: string, userEmail?: string, userName?: string) =>
      deleteItem(accessToken, getBaseURL(appContext), branch, sdId, userEmail, userName),
    importSpreadsheets: (
      branch: string,
      hubSpreadsheetUrl?: string,
      customSpreadsheetUrls?: string[],
      userEmail?: string,
      userName?: string,
      toolboxBuildId?: string,
      toolboxBuildBranch?: string,
      mappingData?: IStaticDataFileMapping[],
    ) =>
      importSpreadsheets(
        accessToken,
        getBaseURL(appContext),
        branch,
        hubSpreadsheetUrl,
        customSpreadsheetUrls,
        userEmail,
        userName,
        toolboxBuildId,
        toolboxBuildBranch,
        mappingData,
      ),
    deployImportedData: (branch: string, userEmail?: string, userName?: string, environmentName?: string) =>
      deployImportedData(accessToken, getBaseURL(appContext), branch, userEmail, userName, environmentName),
    listItems: (branch: string, userEmail?: string, userName?: string) =>
      listItems(accessToken, getBaseURL(appContext), branch, userEmail, userName),
    getDiff: (branch: string, userEmail?: string, userName?: string) =>
      getDiff(accessToken, getBaseURL(appContext), branch, userEmail, userName),
    getLatestReports: (branch: string, userEmail?: string, userName?: string) =>
      getLatestReports(accessToken, getBaseURL(appContext), branch, userEmail, userName),
  };
};
